import { SimulatorBoundary, SimulatorLoanConditions } from '@nemuru-eng/checkout-js';
import { EditableField, FeePaymentType, LenderName, PaymentMethodType } from '@nemuru-eng/js-api-client';

import { isFinancingFieldEditable } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/maps/financingEditableFields.map';
import { EditAutoPaymentMethodFinancingFields } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/schemas/editAutoPaymentMethodForm.schema';
import { FinancingLiveSummaryData } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/types/FinancingLiveSummaryData';

export const financingSummaryToFormFields = (
  financingSummary: FinancingLiveSummaryData,
): EditAutoPaymentMethodFinancingFields => ({
  term: financingSummary.term,
  instalmentAmount: financingSummary.instalmentAmount,
  downPaymentAmount: financingSummary.downPaymentAmount,
  principalAmount: financingSummary.principalAmount,
  discountAmount: financingSummary.discountAmount,
  annualInterest: financingSummary.annualInterest,
  insurance: financingSummary.insurance,
  insuranceProvider: financingSummary.insuranceProvider,
  setupFeeAmount: financingSummary.setupFeeAmount,
  setupFeeType: financingSummary.setupFeeType,
});

export const mapSimulatorConditionsToFinancingFields = (
  simulatorConditions: SimulatorLoanConditions[] | null,
): (EditAutoPaymentMethodFinancingFields & { lenders: LenderName[] }) | null => {
  const selectedCondition = simulatorConditions?.[0];

  if (!selectedCondition) {
    return null;
  }

  const setupFeeType = selectedCondition.setupFeeType;

  return {
    term: selectedCondition.term ?? null,
    instalmentAmount: selectedCondition.instalmentTotalAmount?.value ?? null,
    downPaymentAmount: selectedCondition.downPayment?.value ?? null,
    principalAmount: selectedCondition.principal?.value ?? null,
    discountAmount: selectedCondition.discount?.value ?? null,
    annualInterest: selectedCondition.annualInterest?.value ?? null,
    insurance: selectedCondition.insurance?.type ?? null,
    insuranceProvider: selectedCondition.insurance?.provider ?? null,
    setupFeeAmount: selectedCondition.setupFeeAmount?.value ?? null,
    setupFeeType: setupFeeType && Object.values(FeePaymentType).includes(setupFeeType) ? setupFeeType : null,
    lenders: [
      ...new Set(
        (simulatorConditions ?? [])
          .map((condition) => condition.lenderName)
          .filter((lender): lender is LenderName => Boolean(lender)),
      ),
    ],
  };
};

export const buildEditFinancingSimulatorBoundaries = ({
  editableFields,
  lenderName,
  assetAmount,
  registrationDate,
  financingDefaults,
  principal,
  dateOfBirth,
}: {
  editableFields: EditableField[];
  assetAmount: number | undefined;
  registrationDate: Date | null | undefined;
  financingDefaults: FinancingLiveSummaryData;
  principal?: number;
  lenderName: LenderName | undefined;
  dateOfBirth?: Date;
}): SimulatorBoundary[] => {
  const boundary: SimulatorBoundary & {
    principal?: number;
    terms?: number[];
    annualInterests?: number[];
    insuranceTypes?: FinancingLiveSummaryData['insurance'][];
    setupFees?: Array<'has_setup_fee' | 'has_no_setup_fee'>;
    discount?: number;
  } = {
    paymentMethodType: PaymentMethodType.AUTO_LOAN,
    ...(assetAmount !== undefined && { assetAmount }),
    ...(registrationDate !== undefined && { registrationDate: registrationDate ?? null }),
    ...(principal !== undefined && { principal }),
    ...(dateOfBirth && { dateOfBirth }),
    ...(financingDefaults.downPaymentAmount != null && {
      downPayment: financingDefaults.downPaymentAmount,
    }),
  };

  if (!isFinancingFieldEditable(editableFields, EditableField.TERM) && financingDefaults.term !== null) {
    boundary.terms = [financingDefaults.term];
  }

  if (lenderName) {
    boundary.lenders = [lenderName];
  }

  if (
    !isFinancingFieldEditable(editableFields, EditableField.ANNUAL_INTEREST) &&
    financingDefaults.annualInterest !== null
  ) {
    boundary.annualInterests = [financingDefaults.annualInterest];
  }

  if (!isFinancingFieldEditable(editableFields, EditableField.INSURANCE) && financingDefaults.insurance) {
    boundary.insuranceTypes = [financingDefaults.insurance];
  }

  if (!isFinancingFieldEditable(editableFields, EditableField.SETUP_FEE)) {
    const hasSetupFee = (financingDefaults.setupFeeAmount ?? 0) > 0;
    boundary.setupFees = [hasSetupFee ? 'has_setup_fee' : 'has_no_setup_fee'];
  }

  if (!isFinancingFieldEditable(editableFields, EditableField.DISCOUNT) && financingDefaults.discountAmount !== null) {
    boundary.discount = financingDefaults.discountAmount;
  }

  return [boundary];
};
